<template>
  <div class="v2-activities">
    <!-- Tabs — single always-mounted indicator slides between tab positions
         (mirrors framer-motion's `layoutId` sliding underline in the Lovable
         design). Position is tracked via ref on the active button. -->
    <div class="v2-activities__tabs" role="tablist">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        :ref="(el) => { if (el) tabRefs[tab.key] = el }"
        type="button"
        role="tab"
        :aria-selected="activeTab === tab.key"
        class="v2-activities__tab"
        :class="{ 'is-active': activeTab === tab.key }"
        @click="setActiveTab(tab.key)"
      >
        {{ tab.label }}
      </button>
      <span
        v-show="indicatorStyle.width"
        class="v2-activities__tab-indicator"
        :style="indicatorStyle"
        aria-hidden="true"
      />
    </div>

    <!-- Stable-height wrapper. min-height is locked across a tab swap so
         the document doesn't shrink while the new fetch is in flight,
         which is what was snapping the scroll position upward. -->
    <div
      ref="content"
      class="v2-activities__content"
      :class="{ 'is-refetching': isRefetching }"
      :style="contentMinHeight ? { minHeight: contentMinHeight } : null"
    >
    <!-- Loading (initial) -->
    <div v-if="isLoading && activities.length === 0" class="v2-activities__loading">
      <div class="v2-activities__spinner" aria-hidden="true" />
      <p>Loading activities&hellip;</p>
    </div>

    <!-- List -->
    <div v-else-if="filteredActivities.length > 0" class="v2-activities__list">
      <article
        v-for="(activity, i) in filteredActivities"
        :key="activity.id || (activity.action + '-' + i)"
        class="v2-activities__item"
        :style="{ animationDelay: (i * 80) + 'ms' }"
      >
        <div
          class="v2-activities__icon"
          :class="'v2-activities__icon--' + config(activity).tone"
          aria-hidden="true"
          v-html="iconSvg(config(activity).icon)"
        />

        <div class="v2-activities__body">
          <div class="v2-activities__body-row">
            <span class="v2-activities__user">
              {{ activity.subject || 'Anonymous' }}
            </span>
            <span class="v2-activities__time">{{ formatTime(activity.created_at) }}</span>
          </div>
          <p v-if="messageOf(activity)" class="v2-activities__message">
            {{ messageOf(activity) }}
          </p>
          <a
            v-if="activity.action === 'campaign_created' && activity.sub_campaign_id && String(activity.sub_campaign_id) !== String(campaignId)"
            :href="'/campaigns/' + activity.sub_campaign_id"
            class="v2-activities__link"
          >
            View campaign &rarr;
          </a>
        </div>

        <span
          v-if="amountOf(activity)"
          class="v2-activities__amount"
        >
          {{ amountOf(activity) }}
        </span>
      </article>

      <button
        v-if="next_page_url"
        type="button"
        :disabled="isLoadingMore"
        class="v2-activities__load-more"
        @click="loadMore"
      >
        <span v-if="isLoadingMore">Loading&hellip;</span>
        <span v-else>Load more</span>
      </button>
    </div>

    <!-- Empty -->
    <div v-else class="v2-activities__empty">
      <p>
        Be the first to
        <a :href="'/donate-to/' + campaignId" class="v2-activities__empty-link">donate</a>
        or
        <a :href="fundraiseUrl" class="v2-activities__empty-link">create your own fundraising page</a>
        and support this campaign.
      </p>
    </div>
    </div>
  </div>
</template>

<script>
// ─── Icon catalog — paths copied verbatim from Lucide v0.462.0,
// the same release bundled with the Lovable reference design. ───────
const ICON_SVG = {
  heart:
    '<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>',
  users:
    '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>' +
    '<circle cx="9" cy="7" r="4"/>' +
    '<path d="M22 21v-2a4 4 0 0 0-3-3.87"/>' +
    '<path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  star:
    '<path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"/>',
}

// Activity action → visual config. Collapses the 5 legacy action types
// into Lovable's 3 semantic buckets: donation (pink heart), campaign
// (blue users), and milestone (navy star).
//   tone: CSS modifier for the icon circle color
//   icon: key into ICON_SVG above
const ACTIVITY_CONFIG = {
  donation_created:         { tone: 'pink', icon: 'heart' },
  // Honoree donations are still donations — bucket them with Heart pink.
  // The "In memory of / In honor of" prefix in the message carries the
  // semantic distinction.
  honoree_donation_created: { tone: 'pink', icon: 'heart' },
  campaign_created:         { tone: 'blue', icon: 'users' },
  // Pledges are commitments / progress milestones — Star navy.
  pledge_created:           { tone: 'navy', icon: 'star'  },
  pledge_activity_created:  { tone: 'navy', icon: 'star'  },
}
const DEFAULT_CONFIG = { tone: 'navy', icon: 'star' }

// Per-tab API query-string value for /api/open/campaigns/activity.
// The endpoint filters at the DB level when this is set — important
// because the list is paginated (5 per page) and donations outnumber
// campaigns ~100:1. Without server-side filtering, the Campaigns tab
// would appear empty until the user clicks "Load more" many times.
const TAB_API_FILTER = {
  all:       null,
  donors:    'donation',
  campaigns: 'campaign',
}

export default {
  name: 'V2CampaignActivities',

  props: {
    campaignId: { type: [String, Number], required: true },
  },

  data() {
    return {
      activeTab: 'all',
      activities: [],
      isLoading: true,
      isLoadingMore: false,
      // True while a tab-switch (or post-donation) refetch is in flight.
      // We keep the previously rendered list visible during this window
      // so the document doesn't shrink and snap the scroll upward.
      isRefetching: false,
      // px value applied as `min-height` on the content wrapper across
      // a tab swap. Captured before the new render, released a frame
      // after the new render paints.
      contentMinHeight: null,
      // Seeded by created(); always read via buildActivityUrl() so the
      // tab-specific filter is applied.
      next_page_url: null,
      tabs: [
        { key: 'all',       label: 'All' },
        { key: 'donors',    label: 'Donors' },
        { key: 'campaigns', label: 'Campaigns' },
      ],
      // Populated by function refs on each tab button; read in measureIndicator.
      tabRefs: {},
      indicatorStyle: { left: '0px', width: '0px' },
    }
  },

  computed: {
    // The list is now pre-filtered server-side per tab, so the client
    // just renders whatever the fetch returned. Retained as a computed
    // to keep the template stable if the tab-filter strategy changes.
    filteredActivities() {
      return this.activities
    },
    fundraiseUrl() {
      return '/create-my-campaign/' + this.campaignId
    },
  },

  created() {
    // Plain (non-reactive) counter used to discard out-of-order fetches
    // when the user clicks tabs faster than the network responds.
    this._requestToken = 0
    this.next_page_url = this.buildActivityUrl()
    this.loadActivities()
    // If the donation widget fires a success event, re-fetch so the
    // donor sees their contribution show up immediately.
    this.emitter.on('campaign-donation-update', this.onDonationUpdate)
  },

  mounted() {
    // First paint positions the underline under the initial active tab.
    this.$nextTick(this.measureIndicator)
    // Recalculate on resize (fonts may reflow tab widths).
    this._onResize = () => this.measureIndicator()
    window.addEventListener('resize', this._onResize)
  },

  beforeUnmount() {
    this.emitter.off('campaign-donation-update', this.onDonationUpdate)
    window.removeEventListener('resize', this._onResize)
  },

  methods: {
    config(activity) {
      return ACTIVITY_CONFIG[activity.action] || DEFAULT_CONFIG
    },

    setActiveTab(key) {
      if (this.activeTab === key) return
      // Lock the rendered height of the content wrapper BEFORE we
      // start the new fetch. The previous list stays mounted (just
      // dimmed) so the document height can't shrink while we wait
      // for the new data — that shrink is what was snapping the
      // viewport upward.
      this.lockContentHeight()
      this.activeTab = key
      this.isRefetching = true
      // Wait for the `is-active` color swap to commit, then slide.
      this.$nextTick(this.measureIndicator)
      // Refetch with the tab-specific filter so supporting campaigns
      // actually surface in the Campaigns tab (pagination would
      // otherwise hide them behind pages of donations).
      this.next_page_url = this.buildActivityUrl()
      this.loadActivities({ replace: true })
    },

    lockContentHeight() {
      const el = this.$refs.content
      if (el) this.contentMinHeight = el.offsetHeight + 'px'
    },

    releaseContentHeight() {
      // Hold the lock for one paint after the new content is in the
      // DOM, then release. The double-rAF guarantees the new layout
      // has been measured before we let it resize the wrapper.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => { this.contentMinHeight = null })
      })
    },

    // Builds the current-tab URL. Split out so loadActivities, the
    // initial load, and the donation-update re-fetch all agree.
    buildActivityUrl() {
      const base = '/api/open/campaigns/activity/' + this.campaignId
      const filter = TAB_API_FILTER[this.activeTab]
      return filter ? `${base}?activity=${filter}` : base
    },

    measureIndicator() {
      const el = this.tabRefs[this.activeTab]
      if (!el || !el.offsetParent) return
      this.indicatorStyle = {
        left: el.offsetLeft + 'px',
        width: el.offsetWidth + 'px',
      }
    },

    iconSvg(name) {
      // Wrap the raw Lucide path strings in a real <svg> element so
      // v-html renders them inside the div's HTML body. Without this
      // wrapper the browser treats <path> as an unknown HTML tag and
      // skips rendering entirely — nothing shows.
      //
      // width/height are set as SVG attributes (not CSS) because Vue's
      // scoped CSS compiles `.icon svg` to `.icon svg[data-v-*]`, and
      // the v-html'd svg element does NOT carry the data-v attribute,
      // so any `.icon svg { width: ... }` rule is ignored.
      const inner = ICON_SVG[name] || ICON_SVG.star
      return (
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" '
        + 'width="20" height="20" '
        + 'fill="none" stroke="currentColor" stroke-width="2" '
        + 'stroke-linecap="round" stroke-linejoin="round">'
        + inner
        + '</svg>'
      )
    },

    messageOf(activity) {
      // Prefer the explicit message field; fall back to the legacy
      // meta_data.message_to_recipient on honor donations.
      if (activity.action === 'campaign_created') {
        return `Started a campaign for ${activity.campaignName}`
      }
      if (activity.action === 'honoree_donation_created') {
        // The stored activity already includes its dedication prefix.
        if (typeof activity.sub_message === 'string' && activity.sub_message.trim()) {
          return activity.sub_message
        }

        const name = typeof activity.honoreeName === 'string' ? activity.honoreeName.trim() : ''
        if (!name) return 'A tribute donation'
        if (activity.dedication === 'in_memory_of') return `In memory of ${name}`
        if (activity.dedication === 'in_honor_of') return `In honor of ${name}`
        // Missing legacy details must not turn a memorial into an honor gift.
        return `In tribute to ${name}`
      }
      if (activity.action === 'donation_created') {
        return activity.meta_data?.message_to_recipient || null
      }
      if (activity.action === 'pledge_activity_created') {
        return activity.message || null
      }
      return activity.message || null
    },

    amountOf(activity) {
      if (activity.action === 'donation_created') {
        return this.extractDollar(activity.message)
      }
      if (activity.action === 'honoree_donation_created') {
        return activity.donationAmount || null
      }
      if (activity.action === 'campaign_created' && activity.campaignGoal) {
        // For new campaigns, show the goal as a secondary number
        return '$' + Number(activity.campaignGoal).toLocaleString()
      }
      return null
    },

    extractDollar(message) {
      if (typeof message !== 'string') return null
      const m = message.match(/\$\d+(?:,\d{3})*(?:\.\d{1,2})?/)
      return m ? m[0] : null
    },

    formatTime(timestamp) {
      if (!timestamp) return ''
      const d = new Date(timestamp)
      if (isNaN(d.getTime())) return timestamp
      const diffMs = Date.now() - d.getTime()
      const mins = Math.round(diffMs / 60000)
      if (mins < 1) return 'just now'
      if (mins < 60) return `${mins} min ago`
      const hrs = Math.round(mins / 60)
      if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`
      const days = Math.round(hrs / 24)
      if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    },

    onDonationUpdate() {
      // Same height-lock + replace pattern as a tab switch so the
      // post-donation refresh doesn't flash the list either.
      this.lockContentHeight()
      this.isRefetching = true
      this.next_page_url = this.buildActivityUrl()
      this.loadActivities({ replace: true })
    },

    async loadActivities({ replace = false } = {}) {
      if (!this.next_page_url) return
      const url = this.next_page_url
      const token = ++this._requestToken
      try {
        const { data } = await axios.get(url)
        // Discard if a newer request superseded this one (rapid tab clicks).
        if (token !== this._requestToken) return
        if (!data || !Array.isArray(data.data)) {
          if (replace) this.activities = []
          return
        }

        // Resolve honoree donation details in parallel (legacy behavior)
        const enriched = await Promise.all(
          data.data.map((a) =>
            a && a.action === 'honoree_donation_created' && a.donation_id
              ? this.enrichHonoree(a)
              : a
          )
        )

        if (token !== this._requestToken) return

        // Filter out the creation row for the current campaign itself
        const cleaned = enriched.filter(
          (a) => a && !(a.action === 'campaign_created' && String(a.sub_campaign_id) === String(this.campaignId))
        )

        if (replace) {
          this.activities = cleaned
        } else {
          this.activities.push(...cleaned)
        }
        this.next_page_url = data.next_page_url
      } catch (err) {
        console.error('V2CampaignActivities: loadActivities failed', err)
      } finally {
        if (token === this._requestToken) {
          this.isLoading = false
          this.isLoadingMore = false
          if (this.isRefetching) {
            this.isRefetching = false
            this.releaseContentHeight()
          }
        }
      }
    },

    async enrichHonoree(activity) {
      try {
        const { data } = await axios.get(`/api/open/donations/${activity.donation_id}`)
        return {
          ...activity,
          donationAmount: data.amount ? '$' + Number(data.amount).toFixed(2) : null,
          dedication: data.dedication,
          honoreeName: data.honoree_name,
        }
      } catch {
        return activity
      }
    },

    async loadMore() {
      if (this.isLoadingMore || !this.next_page_url) return
      this.isLoadingMore = true
      await this.loadActivities()
    },
  },
}
</script>

<style scoped>
.v2-activities {
  font-family: 'Red Hat Display', sans-serif;
}

/* ─── Tabs ────────────────────────────────────────────────── */
/* The tabs row is `position: relative` so the sliding underline is
   positioned against the row, not each individual button. */
.v2-activities__tabs {
  position: relative;
  display: flex;
  justify-content: center;
  gap: 32px;
  border-bottom: 1px solid var(--sideout-border);
  margin-bottom: 32px;
}
.v2-activities__tab {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0 0 12px;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--sideout-muted);
  transition: color 200ms;
}
.v2-activities__tab:hover { color: var(--sideout-navy); }
.v2-activities__tab.is-active { color: var(--sideout-pink); }

/* Single always-mounted underline. `left`/`width` are set by
   measureIndicator() in JS. The cubic-bezier approximates a
   framer-motion spring with stiffness:400, damping:30. */
.v2-activities__tab-indicator {
  position: absolute;
  bottom: -1px;
  height: 2px;
  background: var(--sideout-pink);
  border-radius: 2px;
  transition:
    left 380ms cubic-bezier(0.34, 1.56, 0.64, 1),
    width 380ms cubic-bezier(0.34, 1.56, 0.64, 1);
  pointer-events: none;
}

/* ─── Content wrapper ─────────────────────────────────────────
   Holds a `min-height` lock during a tab swap so the document
   doesn't shrink and snap the scroll position upward while the
   new fetch is in flight. The `is-refetching` modifier dims the
   stale content so the user has subtle feedback that something
   is loading without a hard layout change. */
.v2-activities__content {
  position: relative;
}
.v2-activities__content > * {
  transition: opacity 180ms ease;
}
.v2-activities__content.is-refetching > * {
  opacity: 0.55;
  pointer-events: none;
}

/* ─── Activity list ───────────────────────────────────────── */
.v2-activities__list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 42rem; /* max-w-2xl */
  margin: 0 auto;
}

/* Item row — matches Lovable: initial {opacity:0, x:-20px} → animate
   {opacity:1, x:0}, stagger of i*80ms. */
.v2-activities__item {
  background: #ffffff;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  opacity: 0;
  transform: translateX(-20px);
  animation: v2-item-slide 420ms cubic-bezier(0.16, 1, 0.3, 1) both;
}
@keyframes v2-item-slide {
  from { opacity: 0; transform: translateX(-20px); }
  to   { opacity: 1; transform: translateX(0); }
}

/* Icon circle */
.v2-activities__icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.v2-activities__icon svg {
  width: 20px;
  height: 20px;
  stroke: currentColor;
  fill: none;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}
/* Only 3 tones, matching the Lovable reference. */
.v2-activities__icon--pink { background: rgba(218, 24, 132, 0.1); color: var(--sideout-pink); }
.v2-activities__icon--blue { background: rgba(0, 156, 222, 0.1);  color: #009cde; }
.v2-activities__icon--navy { background: rgba(29, 37, 73, 0.08);  color: var(--sideout-navy); }

/* Body */
.v2-activities__body {
  flex: 1;
  min-width: 0;
}
.v2-activities__body-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.v2-activities__user {
  font-weight: 700;
  color: var(--sideout-navy);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.v2-activities__time {
  font-size: 12px;
  color: var(--sideout-muted);
  flex-shrink: 0;
}
.v2-activities__message {
  margin: 4px 0 0;
  font-size: 14px;
  line-height: 1.45;
  color: var(--sideout-muted);
}
.v2-activities__link {
  display: inline-block;
  margin-top: 4px;
  font-size: 13px;
  font-weight: 600;
  color: var(--sideout-pink);
  text-decoration: none;
}
.v2-activities__link:hover {
  text-decoration: underline;
}

/* Amount — top-aligned alongside icon + body, mirroring Lovable's
   items-start parent. Tabular-nums keeps digits in a fixed grid when
   multiple rows are visible. Single pink color matching Lovable. */
.v2-activities__amount {
  flex-shrink: 0;
  font-weight: 700;
  color: var(--sideout-pink);
  font-variant-numeric: tabular-nums;
}

/* Loading / empty states */
.v2-activities__loading,
.v2-activities__empty {
  max-width: 42rem;
  margin: 0 auto;
  padding: 32px;
  text-align: center;
  color: var(--sideout-muted);
  background: #ffffff;
  border-radius: 16px;
  border: 1px dashed var(--sideout-border);
}
.v2-activities__empty-link {
  color: var(--sideout-pink);
  text-decoration: underline;
}
.v2-activities__spinner {
  width: 28px;
  height: 28px;
  border-radius: 9999px;
  border: 3px solid var(--sideout-border);
  border-top-color: var(--sideout-pink);
  margin: 0 auto 12px;
  animation: v2-spin 0.8s linear infinite;
}
@keyframes v2-spin {
  to { transform: rotate(360deg); }
}

/* Load more */
.v2-activities__load-more {
  margin: 8px auto 0;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 10px 24px;
  background: transparent;
  border: 2px solid var(--sideout-border);
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--sideout-muted);
  cursor: pointer;
  transition: border-color 150ms, color 150ms, background 150ms;
}
.v2-activities__load-more:hover:not(:disabled) {
  color: var(--sideout-pink);
  border-color: var(--sideout-pink);
  background: rgba(218, 24, 132, 0.04);
}
.v2-activities__load-more:disabled {
  opacity: 0.6;
  cursor: wait;
}

@media (prefers-reduced-motion: reduce) {
  .v2-activities__item { animation-duration: 80ms !important; }
  .v2-activities__tab-indicator {
    transition-duration: 80ms !important;
  }
  .v2-activities__spinner { animation-duration: 2.4s; }
}
</style>
